import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { api, type ChatReminder } from "@/api/client";
import { useAuth } from "@/auth/AuthContext";
import { Icon } from "@/components/Icon";
import { useRealtime, type RealtimeEvent } from "@/lib/realtime";
import { isMobilePath } from "@/mobile/device";
import { ReminderFlags, useReminderAction } from "@/pages/Chats/ReminderCard";
import {
  fmtDue,
  reminderChatLink,
  snoozeDue,
  toastEventMatters,
  toastKey,
  visibleToasts,
} from "@/pages/Chats/reminders";
import "./reminderToast.css";

// Наступившие напоминания — всплывашками справа сверху на любой странице
// (рядом с InboundCallHost в AppShell; в мобильной версии — в её оболочке
// MobileApp, там стопка во всю ширину сверху). Только свои: уведомляется тот, кто
// принял предложение или завёл напоминание (`user_id`), а у поставленного
// автоматически — тот, кому его поставили; остальные видят его лишь в чате.
// Принимать здесь нечего: всплывают только активные.
//
// Источник — снимок GET /chat/reminders/mine?due: при загрузке, при каждом
// (пере)подключении потока (за разрыв события потеряны) и по SSE `reminder`
// про моё напоминание или одно из тех, что уже на экране (его могли закрыть в
// другой вкладке или другой рекрутёр). Само событие текста не несёт — он
// приходит только снимком.
//
// Крестик прячет всплывашку до перезагрузки страницы (в памяти): напоминание
// остаётся наступившим — красная метка в списке чатов и карточка в чате.
// Перенесённое «на час» и снова наступившее всплывёт опять (reminders.toastKey).
//
// Стопка — там же, где входящие звонки, но слоем ниже (reminderToast.css):
// звонящий ждёт ответа прямо сейчас, а напоминание подождёт под его
// всплывашкой.
//
// Вернулись на вкладку (телефон разбудил приложение, ноутбук открыли) —
// снимок перечитывается: iOS замораживает страницу в фоне, поток молча стоит,
// и наступившее за это время иначе не всплыло бы до следующего события.

/** Сколько ждать хвоста пачки событий: рассылка наступивших публикует их
 *  разом, по одному на напоминание, — перечитать хватит одного раза. */
const COALESCE_MS = 300;
/** Короче этого в фоне — ничего не пропустили: поток жив. */
const AWAY_MS = 5000;

export function ReminderToastHost() {
  const { state: auth } = useAuth();
  const meRef = useRef<string | null>(null);
  meRef.current = auth.status === "authed" ? auth.user.id : null;
  const navigate = useNavigate();
  const location = useLocation();
  const mobile = isMobilePath(location.pathname);

  const [items, setItems] = useState<ChatReminder[]>([]);
  const [hidden, setHidden] = useState<ReadonlySet<string>>(() => new Set());
  const shownRef = useRef<ReadonlySet<string>>(new Set());
  shownRef.current = new Set(items.map((r) => r.id));

  const loadSeq = useRef(0);
  const load = useCallback(() => {
    const seq = ++loadSeq.current;
    api.myReminders({ due: true }).then(
      (r) => {
        // Ответ на более ранний запрос пришёл позже — он старее того, что есть.
        if (seq === loadSeq.current) setItems(Array.isArray(r.reminders) ? r.reminders : []);
      },
      // Старый chat-service без ручки или нет сети — всплывашек просто нет;
      // следующее переподключение потока спросит снова.
      () => undefined,
    );
  }, []);
  useEffect(load, [load]);

  const timer = useRef<number | undefined>(undefined);
  useEffect(() => () => window.clearTimeout(timer.current), []);
  const onEvent = (ev: RealtimeEvent) => {
    if (!toastEventMatters(ev, meRef.current, shownRef.current)) return;
    window.clearTimeout(timer.current);
    timer.current = window.setTimeout(load, COALESCE_MS);
  };
  useRealtime(onEvent, load);
  useEffect(() => {
    let hiddenAt = 0;
    const onVisibility = () => {
      if (document.visibilityState === "hidden") {
        hiddenAt = Date.now();
        return;
      }
      const away = hiddenAt ? Date.now() - hiddenAt : 0;
      hiddenAt = 0;
      if (away >= AWAY_MS) load();
    };
    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("online", load);
    return () => {
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("online", load);
    };
  }, [load]);

  const hide = (r: ChatReminder) => setHidden((prev) => new Set(prev).add(toastKey(r)));
  const toasts = visibleToasts(items, hidden);

  // Контейнер в DOM всегда, даже пустой: живую область, вставленную вместе с
  // содержимым, дикторы часто не объявляют.
  return (
    <div className="rtoast-stack" role="region" aria-label="Напоминания" aria-live="polite">
      {toasts.map((r) => (
        <ReminderToast
          key={toastKey(r)}
          reminder={r}
          onOpen={() => {
            hide(r);
            // Полная версия — `?person=` инбокса, телефон — слой `?chat=`
            // мобильной (reminders.reminderChatLink).
            const to = r.person_id && reminderChatLink(r.person_id, mobile, location.search);
            if (to) navigate(to);
          }}
          onHide={() => hide(r)}
          onChanged={load}
        />
      ))}
    </div>
  );
}

export function ReminderToast({
  reminder: r,
  onOpen,
  onHide,
  onChanged,
}: {
  reminder: ChatReminder;
  onOpen: () => void;
  onHide: () => void;
  onChanged: () => void;
}) {
  const { busy, error, run } = useReminderAction(onChanged);
  const who = r.person_name || "Без имени";
  return (
    <section className="rtoast" aria-label={`Напоминание: ${who}`}>
      <div className="rtoast-main">
        <span className="rtoast-ic" aria-hidden="true">
          <Icon name="bell" size={20} strokeWidth={1.8} />
        </span>
        <div className="rtoast-text">
          <div className="rtoast-eyebrow">
            Напоминание · <span className="rtoast-when">{fmtDue(r.due_at)}</span>
          </div>
          <div className="rtoast-title" title={who}>
            {who}
          </div>
          <div className="rtoast-body">{r.text}</div>
          <ReminderFlags reminder={r} className="rtoast-flags" />
          {error && (
            <div className="rtoast-err" role="alert">
              {error}
            </div>
          )}
        </div>
        <button
          type="button"
          className="rtoast-x"
          onClick={onHide}
          aria-label="Скрыть"
          title="Скрыть до перезагрузки — напоминание останется в чате"
        >
          <Icon name="close" size={14} />
        </button>
      </div>
      <div className="rtoast-actions">
        <button type="button" className="rtoast-open" onClick={onOpen} disabled={!r.person_id}>
          <Icon name="message" size={15} strokeWidth={1.8} />
          Открыть чат
        </button>
        <button
          type="button"
          className="rtoast-btn"
          disabled={busy}
          onClick={() => void run(() => api.chatReminderDone(r.id))}
        >
          Сделано
        </button>
        <button
          type="button"
          className="rtoast-btn"
          disabled={busy}
          onClick={() => void run(() => api.chatReminderUpdate(r.id, { due_at: snoozeDue() }))}
        >
          Через час
        </button>
      </div>
    </section>
  );
}
